import { Button, TextField } from '@mui/material'
import SmartTextField from '../smarttextfield/SmartTextField'
import { useSmartTranslation } from '../hooks/useSmartTranslation'
import { useEffect, useState } from 'react'
import { apiPost } from '../Api_Functions'
import AlertUI from '../AlertUI'
import SmartSelect from '../smartSelect/SmartSelect'

/**
 * @param {Object} props
 * @param {import('../utilities/objectStructures/mail/mailTemplate').MailTemplate} props.template
 * @param {import('../utilities/objectStructures/globalStructures').NameValue[]} props.data
 * @param {import("../utilities/objectStructures/globalStructures").IdName[]} props.attachmentOptions
 * @param {boolean} props.canEditContent
 * @param {Function} props.onSave Will be called when the mail is sent successfully
 */
const MailForm = ({ template, data = [], attachmentOptions = [], canEditContent = true, onSave = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [AlertElementEmail, showAlertEmail] = AlertUI()
  const [sending, setSending] = useState(false)
  const [newEmail, setNewEmail] = useState(null)
  const [mail, setMail] = useState({
    to: [],
    subject: '',
    templateName: '',
    body: [],
    attachments: []
  })

  useEffect(() => {
    setMail(prev => ({
      ...prev,
      to: Array.isArray(template?.to) ? template.to : [],
      subject: template?.subject || '',
      templateName: template?.templateName || '',
      body: Array.isArray(data) ? data : [],
      attachments: Array.isArray(template?.attachments) ? template.attachments : []
    }))
  }, [template, data])

  const buildBodyFromData = (data) => {
    const obj = {}
    data?.forEach(item => {
      obj[item.name] = item.value
    })
    return obj
  }

  const checkToEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return false
    }
    return true
  }

  const sendMail = async () => {
    if (!mail.to?.length || !mail.subject || !mail.body) {
      showAlert({ severity: 'error', title: t_('Missing required fields') })
      return
    }
    // Check email format
    for (const recipient of mail.to) {
      if (!checkToEmail(recipient)) {
        showAlert({ severity: 'error', title: t_(`Invalid email format: ${recipient}`) })
        return
      }
    }
    setSending(true)

    let payload = null
    if (mail.templateName !== null && mail.templateName !== '') {
      payload = {
        ...mail,
        body: null,
        templateVariables: buildBodyFromData(mail.body)
      }
    } else {
      payload = {
        ...mail,
        templateName: null,
        templateVariables: null
      }
    }

    await apiPost('sendMailTemplate', payload)
      .then(() => {
        showAlert({ severity: 'success', title: t_('Email sent') })
        onSave()
      })
      .catch(error => {
        const backendMessage = error?.response?.data?.message || error?.response?.data || error.message
        showAlert({
          severity: 'error',
          title: t_('Failed to send email:'),
          message: t_(backendMessage)
        })
      })
      .finally(() => {
        setSending(false)
      })
  }

  const addNewEmail = () => {
    return (
      <>
        {AlertElementEmail}
        <TextField
          label={t_('New email')}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          fullWidth
        />
      </>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {AlertElement}
      <SmartSelect
        label={t_('To')}
        selectableOptions={template?.to || []}
        value={mail.to || []}
        onChange={(newValues) => {
          setMail({ ...mail, to: newValues })
        }}
        required
        multiple
        addButtonVisible
        addDialogAccept
        addDialogClose
        content={addNewEmail()}
        onAddDialogAccept={() => {
          if (!checkToEmail(newEmail)) {
            showAlertEmail({ severity: 'error', title: t_('Invalid email format') })
            return false
          }
          if (newEmail && !mail.to.find(e => e.value === newEmail)) {
            const updatedTo = [...mail.to, newEmail]
            setMail({ ...mail, to: updatedTo })
            setNewEmail(null)
            return true
          }
          return false
        }}
      />

      <SmartTextField
        label={t_('Subject')}
        value={mail.subject}
        onChange={(newValue) => {
          setMail({ ...mail, subject: newValue })
        }}
        required
        disabled={!canEditContent}
      />
      <SmartTextField
        label={t_('Body')}
        value={mail.body.map(item => `${item.name}: ${item.value}`).join('\n')}
        onChange={(newValue) => {
          setMail({ ...mail, body: newValue })
        }}
        required
        multiline
        disabled={!canEditContent}
      />
      <SmartSelect
        label={t_('Attachments')}
        selectableOptions={attachmentOptions?.map(item => item.name)}
        value={mail.attachments.map(item => item.name)}
        onChange={(newValue) => {
          const selected = attachmentOptions?.filter(opt =>
            newValue.includes(opt.name)
          )
          setMail({ ...mail, attachments: selected })
        }}
        multiple
      />
      <Button
        variant='contained'
        color='primary'
        onClick={sendMail}
        disabled={sending}
      >
        {sending ? t_('Sending') + '...' : t_('Send')}
      </Button>
    </div>
  )
}

export default MailForm
